<template>
  <v-app>
    <v-main>
      <div class="app-container full-height">
        <DashboardNav v-if="!hideDashboardNav" />
        <div
          class="body-container"
          :class="{
            'hidden-dashboard-nav': hideDashboardNav,
            'minimized-nav': isDashboardNavMinimized,
            'logged-in': isLoggedIn,
            accountProfilePage,
            isDarkMode,
          }"
        >
          <NavBar v-if="showNavBar" :is-mobile="isMobile" />
          <Notifications v-if="isLoggedIn" :is-mobile="isMobile" />
          <router-view :is-mobile="isMobile" />
        </div>
      </div>
    </v-main>
    <SnackBar />
  </v-app>
</template>

<script>
import { mapGetters } from 'vuex'
import { isSoloPage } from '@/lib/utils'

export default {
  components: {
    DashboardNav: () => import('@/components/hud/DashboardNav'),
    NavBar: () => import('@/components/hud/NavBar'),
    SnackBar: () => import('@/components/hud/SnackBar'),
    Notifications: () => import('@/components/hud/Notifications'),
  },
  computed: {
    ...mapGetters(['isLoggedIn', 'isDarkMode', 'isDashboardNavMinimized']),
    isDarkMode() {
      return this.$store.state.isDarkMode
    },
    showNavBar() {
      const excludedViews = []
      return !excludedViews.includes(this.$route.name)
    },
    isMobile() {
      return window.matchMedia('only screen and (max-width: 600px)').matches
    },
    hideDashboardNav() {
      return isSoloPage(this.$route.path)
    },
    accountProfilePage() {
      return this.$route.name === 'account-profile-page'
    },
  },
  watch: {
    $route() {
      // following code is used to measure page load performances
      // 'mark1' indicates a route change
      // 'measures' and mark2,3,4 etc are used to measure times since mark1
      window.performance.clearMarks()
      window.performance.clearMeasures()
      window.performance.mark('mark1')
    },
  },
  async created() {
    await this.$store.dispatch('loadDashboardNavMinimized')
    await this.$store.dispatch('loadDarkMode')
    // Body is not a vue component so we have to manually updated it's bgcolor.
    document.body.className = this.isDarkMode ? 'darkmode' : ''
    this.isMobile ? document.body.classList.add('mobilemode') : null
  },
}
</script>
<style scoped>
.app-container {
  display: flex;
  position: relative;
}

.body-container {
  width: 100%;
}

.body-container.accountProfilePage {
  background-color: var(--paper-background-color);
}
.body-container.accountProfilePage.isDarkMode {
  background-color: #000000;
}

@media screen and (min-width: 960px) {
  .body-container {
    width: 100%;
    width: calc(100vw - 240px);
  }

  .body-container.minimized-nav {
    width: calc(100vw - 60px);
  }

  .body-container.hidden-dashboard-nav {
    width: 100%;
  }
}

.app_container {
  /* max-width: 1300px; */
  min-height: 100vh;
}
@media screen and (max-width: 820px) {
  .container {
    padding: 12px 0;
  }
}
</style>

<style lang="scss">
@import 'https://use.typekit.net/cxe1pkr.css';

body {
  font-size: 13px;
  font-family: -apple-system, BlinkMacSystemFont, Segoe UI, roboto, sans-serif;
  background-color: var(--background-color);
}

#app {
  background: none;
  color: inherit;
  font-family: -apple-system, BlinkMacSystemFont, Segoe UI, roboto, sans-serif;
}

@media (min-width: 960px) {
  .container {
    max-width: 100%;
  }
}

#app .v-ripple__container {
  display: none !important;
}

#app .v-dialog > .v-card > .v-card__title {
  font-weight: 500;
  padding-left: 1.6em;
  padding-top: 30px;
  // background-color: white;
  font-family: erbaum, roboto, sans-serif;
  text-transform: uppercase;
  // border-bottom: 3px solid #424242;
}

#app .v-dialog {
  background-color: var(--paper-background-color);
  margin: 12px;
}

#app .v-dialog:not(.v-dialog--fullscreen) {
  max-height: 98%;
}

//mobile menu drawer
.v-navigation-drawer {
  width: 140px !important;
}
.v-navigation-drawer__content {
  background: var(--paper-background-color);
}

h2 {
  font-family: 'futura-pt', 'erbaum', sans-serif;
  font-weight: 600;
  font-style: normal;
  font-size: 1.1rem;
  margin-bottom: 0.4em;
  color: var(--color-secondary);
}

#app .v-text-field--outlined fieldset {
  color: var(--color-tertiary);
}

#app .v-text-field--filled {
  color: var(--color-tertiary) !important;
}

#app .theme--light.v-input input {
  color: var(--color-secondary);
}

.custom-placeholder-color input::placeholder {
  color: var(--color-tertiary) !important;
}
.theme--light.v-text-field--solo > .v-input__control > .v-input__slot {
  background: var(--paper-background-color) !important;
}
.theme--dark.v-text-field--solo > .v-input__control > .v-input__slot {
  background: var(--paper-background-color) !important;
}
.theme--light.v-select .v-select__selection--comma {
  color: var(--color-primary) !important;
}
.theme--light.v-icon {
  color: var(--color-secondary);
}
.modelSelect.v-text-field.v-text-field--enclosed:not(.v-text-field--rounded)
  > .v-input__control
  > .v-input__slot {
  padding: 0px;
}
.modelSelect {
  background: none;
}
.v-text-field--outlined .v-input__append-outer {
  margin-top: 0px !important;
}
// hides arrows from number input
input[type='number']::-webkit-inner-spin-button,
input[type='number']::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

//---------CUSTOM SCROLLBAR---------
/* width */
// ::-webkit-scrollbar {
//   width: 7px;
//   height: 7px;
// }

// /* Track */
// ::-webkit-scrollbar-track {
//   background: var(--color-very-subtle);
// }

// /* Handle */
// ::-webkit-scrollbar-thumb {
//   background: var(--color-tertiary);
// }

// /* Handle on hover */
// ::-webkit-scrollbar-thumb:hover {
//   background: var(--color-secondary);
// }
//--------------------------------------
</style>
